import React, { useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import PropTypes from 'prop-types';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Spin,
  Button,
  Form,
  Input,
  Select,
  message,
  Typography,
  Space,
  Table,
} from 'antd';
import { addMemberData_API } from '../../../../../functions/membertData';
import { useSelector } from 'react-redux';
import styles from './styles/CreateMemberData.module.css';

const { Option } = Select;
const { Title } = Typography;
const vehicleTypes = ['รถยนต์', 'รถมอเตอร์ไซค์'];

const CreateMemberData = ({ onSuccess, onCancel }) => {
  const [socket, setSocket] = useState(null);
  const [form] = Form.useForm();
  const user = useSelector((state) => state.user);
  const [submitting, setSubmitting] = useState(false);
  const [houseNumberChecking, setHouseNumberChecking] = useState(false);
  const [vehicleCheckingMap, setVehicleCheckingMap] = useState({});
  const [memberCheckStatus, setMemberCheckStatus] = useState(null);
  const [vehicleCheckStatusMap, setVehicleCheckStatusMap] = useState({});

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_SOCKETIO);
    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, []);

  const onHouseNumberChange = useCallback(
    (e) => {
      const houseNumber = e.target.value.trim();
      setHouseNumberChecking(true);
      setMemberCheckStatus(null);

      if (!houseNumber) {
        setHouseNumberChecking(false);
        return;
      }

      const data = { villageId: user.id, houseNumber };

      if (socket) {
        socket.emit('checkHouseNumber', data);
        socket.once('getHouseNumber', (result) => {
          setMemberCheckStatus(result ? 'error' : 'success');
          setHouseNumberChecking(false);
        });
      } else {
        setHouseNumberChecking(false);
      }
    },
    [socket, user.id]
  );

  const onVehicleNumberChange = useCallback(
    (fieldName) => (e) => {
      const vehicleNumber = e.target.value.trim();
      setVehicleCheckingMap((prev) => ({ ...prev, [fieldName]: true }));
      setVehicleCheckStatusMap((prev) => ({ ...prev, [fieldName]: null }));

      if (!vehicleNumber) {
        setVehicleCheckingMap((prev) => ({ ...prev, [fieldName]: false }));
        return;
      }

      const data = { villageId: user.id, vehicleNumber, fieldName };

      if (socket) {
        socket.emit('checkVehicleNumber', data);
        socket.once('getVehicleNumber', (result) => {
          const status = result && result.result ? 'error' : 'success';
          setVehicleCheckStatusMap((prev) => ({ ...prev, [result.fieldName]: status }));
          setVehicleCheckingMap((prev) => ({ ...prev, [result.fieldName]: false }));
        });
      } else {
        setVehicleCheckingMap((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [socket, user.id]
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const newValues = { ...values, userId: user.id };

      setSubmitting(true);
      const res = await addMemberData_API(user.token, newValues);
      message.success(res.message);
      form.resetFields();
      onSuccess(res.data);
    } catch (error) {
      console.error('Error creating member:', error);
      message.error(
        error.response?.data?.message ||
        'เกิดข้อผิดพลาดในการสร้างข้อมูลลูกค้า'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Form
        form={form}
        layout="vertical"
        name="createMemberForm"
        initialValues={{ vehicles: [] }}
        onFinish={handleSubmit}
      >
        <Title level={4} className={styles.title}>
          เพิ่มเลขที่บ้าน
        </Title>
        <Alert
          message="หากไม่ต้องการระบุเลขที่บ้าน สามารถตั้งชื่อสำหรับเลขที่บ้านได้ตามความเหมาะสม"
          type="info"
          showIcon
          className={styles.alert}
        />

        <Form.Item
          name="houseNumber"
          label="เลขที่บ้าน"
          hasFeedback
          validateStatus={memberCheckStatus}
          help={
            memberCheckStatus === 'error'
              ? 'โปรดกรอกเลขที่บ้านที่ไม่ซ้ำกับในระบบ'
              : ''
          }
          rules={[{ required: true, message: 'กรุณากรอกบ้านเลขที่!' }]}
          className={styles.formItem}
        >
          <Input
            placeholder="กรอกเลขที่บ้าน"
            onChange={onHouseNumberChange}
            suffix={houseNumberChecking && <Spin size="small" />}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="สถานะ"
          rules={[{ required: true, message: 'กรุณาเลือกสถานะ!' }]}
          className={styles.formItem}
        >
          <Select placeholder="เลือกสถานะ">
            <Option value="เจ้าของ">เจ้าของ</Option>
            <Option value="คนเช่า">คนเช่า</Option>
          </Select>
        </Form.Item>

        <Form.List name="vehicles">
          {(fields, { add, remove }) => (
            <>
              <Table
                dataSource={fields.map((field) => ({ ...field, key: field.key }))}
                columns={[
                  {
                    title: 'ลำดับ',
                    key: 'index',
                    render: (text, record, index) => index + 1,
                    width: 100,
                    align: 'center',
                  },
                  {
                    title: 'ประเภทรถ',
                    key: 'vehicleType',
                    render: (text, record) => (
                      <Form.Item
                        name={[record.name, 'vehicleType']}
                        rules={[{ required: true, message: 'กรุณาเลือกประเภทรถ!' }]}
                        noStyle
                      >
                        <Select placeholder="เลือกประเภทรถ" style={{ width: '100%' }}>
                          {vehicleTypes.map((type) => (
                            <Option key={type} value={type}>
                              {type}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ),
                    width: 200,
                  },
                  {
                    title: 'หมายเลขป้ายทะเบียนรถ',
                    key: 'vehicleLicensePlate',
                    render: (text, record) => (
                      <Form.Item
                        name={[record.name, 'vehicleLicensePlate']}
                        rules={[{ required: true, message: 'กรุณาใส่หมายเลขป้ายทะเบียนรถ!' }]}
                        hasFeedback
                        validateStatus={vehicleCheckStatusMap[record.name]}
                        help={
                          vehicleCheckStatusMap[record.name] === 'error'
                            ? 'โปรดกรอกเลขทะเบียนรถที่ไม่ซ้ำกับในระบบ'
                            : ''
                        }
                        noStyle
                      >
                        <Input
                          placeholder="กรอกหมายเลขป้ายทะเบียนรถ"
                          onChange={onVehicleNumberChange(record.name)}
                          suffix={vehicleCheckingMap[record.name] && <Spin size="small" />}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    ),
                    width: 250,
                  },
                  {
                    title: 'จังหวัด',
                    key: 'province',
                    render: (text, record) => (
                      <Form.Item
                        name={[record.name, 'province']}
                        rules={[{ required: true, message: 'กรุณากรอกจังหวัด!' }]}
                        noStyle
                      >
                        <Input placeholder="กรอกจังหวัด" style={{ width: '100%' }} />
                      </Form.Item>
                    ),
                    width: 200,
                  },
                  {
                    title: 'จัดการ',
                    key: 'action',
                    render: (text, record) => (
                      <Button
                        type="link"
                        danger
                        onClick={() => remove(record.name)}
                        icon={<CloseOutlined />}
                      >
                        ลบ
                      </Button>
                    ),
                    width: 100,
                    align: 'center',
                  },
                ]}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 600 }}
              />
              <Button
                type="dashed"
                onClick={() => add()}
                icon={<PlusOutlined />}
                className={styles.addButton}
                style={{ marginTop: 16 }}
              >
                เพิ่มรถ
              </Button>
            </>
          )}
        </Form.List>

        <Form.Item className={styles.actionGroup}>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              className={styles.submitButton}
            >
              เพิ่ม
            </Button>
            <Button type="default" onClick={onCancel} className={styles.cancelButton}>
              ยกเลิก
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

CreateMemberData.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreateMemberData;
